import { KeyboardEvent, ReactNode, useRef } from "react";
import styled, { css } from "styled-components";
import { designSystemVars as vars } from "../tokens";
import { ButtonBase } from "./Button";

export type TabsVariant = "pill" | "outlinedPill" | "underline";

export interface TabItem {
  value: string;
  label: ReactNode;
  icon?: ReactNode;
  ariaLabel?: string;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onValueChange: (value: string) => void;
  "aria-label": string;
  variant?: TabsVariant;
  fullWidth?: boolean;
  className?: string;
  preserveDisabledAppearance?: boolean;
}

const TabList = styled.div<{ $variant: TabsVariant; $fullWidth: boolean }>`
  box-sizing: border-box;
  display: flex;
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "fit-content")};
  max-width: 100%;
  align-items: flex-start;
  gap: ${({ $variant }) =>
    $variant === "underline" ? vars.spacing.none : vars.spacing.xs};
  flex-wrap: nowrap;
  justify-content: flex-start;
  border-radius: ${({ $variant }) =>
    $variant === "underline" ? vars.radius.none : vars.radius.lg};
  padding-bottom: ${({ $variant }) =>
    $variant === "underline" ? vars.spacing.xl : vars.spacing.none};
`;

const tabVariantStyles = {
  pill: css<{ $selected: boolean }>`
    min-width: 0;
    height: 40px;
    flex: 0 1 auto;
    gap: ${vars.spacing.xs};
    padding: ${vars.spacing.xs} ${vars.spacing.sm};
    border: 0;
    border-radius: ${vars.radius.xl};
    background: ${({ $selected }) =>
      $selected
        ? vars.color.action.primaryPressed
        : vars.color.background.transparent};
    color: ${({ $selected }) =>
      $selected ? vars.color.text.inverse : vars.color.text.primary};
    font-size: ${vars.typography.size.sm};
    font-weight: ${vars.typography.weight.regular};
    line-height: ${vars.typography.lineHeight.sm};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:not(:disabled):hover {
      background: ${vars.color.action.primary};
      color: ${vars.color.text.inverse};
    }
  `,
  underline: css<{ $selected: boolean }>`
    width: 123px;
    min-height: 32px;
    justify-content: center;
    padding: ${vars.spacing.sm} ${vars.spacing["2xl"]};
    border: 0;
    border-bottom: ${({ $selected }) =>
      $selected
        ? `${vars.borderWidth.thick} solid ${vars.color.border.strong}`
        : `${vars.borderWidth.hairline} solid ${vars.color.border.strong}`};
    background: var(--inzeloop-ds-color-background-transparent);
    color: ${vars.color.text.primary};
    font-size: ${vars.typography.size.sm};
    font-weight: ${vars.typography.weight.regular};
    line-height: ${vars.typography.lineHeight.sm};

    @media (min-width: 834px) and (max-width: 1200px) {
      width: auto;
    }
  `,
};

const TabButton = styled(ButtonBase)<{
  $variant: TabsVariant;
  $selected: boolean;
  $preserveDisabledAppearance: boolean;
}>`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  font-family: ${vars.typography.family.sans};
  cursor: default;

  ${({ $variant }: { $variant: TabsVariant }) =>
    tabVariantStyles[$variant === "outlinedPill" ? "pill" : $variant]}

  ${({ $variant }) =>
    $variant === "outlinedPill" &&
    css`
      border: ${vars.borderWidth.thin} solid;
      border-color: ${({ $selected }: { $selected: boolean }) =>
        $selected
          ? vars.color.action.primaryPressed
          : vars.color.border.medium};
    `}

  &:focus-visible {
    outline: none;
    box-shadow: ${vars.shadow.focus};
  }

  &:disabled {
    color: ${vars.color.text.disabled};
    cursor: not-allowed;
  }

  ${({ $preserveDisabledAppearance, $selected, $variant }) =>
    $preserveDisabledAppearance &&
    css`
      &:disabled {
        color: ${$selected && $variant !== "underline"
          ? vars.color.text.inverse
          : vars.color.text.primary};
      }
    `}

  > svg {
    flex: 0 0 auto;
  }

  svg path {
    stroke: currentColor;
  }
`;

export const Tabs = ({
  items,
  value,
  onValueChange,
  variant = "pill",
  fullWidth = false,
  className,
  preserveDisabledAppearance = false,
  "aria-label": ariaLabel,
}: TabsProps) => {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const moveToTab = (currentIndex: number, direction: 1 | -1) => {
    if (items.length === 0) return;

    let nextIndex = currentIndex;
    do {
      nextIndex = (nextIndex + direction + items.length) % items.length;
    } while (items[nextIndex].disabled && nextIndex !== currentIndex);

    if (!items[nextIndex].disabled) {
      onValueChange(items[nextIndex].value);
      tabRefs.current[nextIndex]?.focus();
    }
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      moveToTab(index, 1);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      moveToTab(index, -1);
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      const enabledIndexes = items
        .map((item, itemIndex) => (item.disabled ? -1 : itemIndex))
        .filter((itemIndex) => itemIndex >= 0);
      const nextIndex =
        event.key === "Home"
          ? enabledIndexes[0]
          : enabledIndexes[enabledIndexes.length - 1];

      if (nextIndex !== undefined) {
        onValueChange(items[nextIndex].value);
        tabRefs.current[nextIndex]?.focus();
      }
    }
  };

  return (
    <TabList
      role="tablist"
      aria-label={ariaLabel}
      $variant={variant}
      $fullWidth={fullWidth}
      className={className}
    >
      {items.map((item, index) => {
        const selected = item.value === value;
        return (
          <TabButton
            key={item.value}
            ref={(element) => {
              tabRefs.current[index] = element;
            }}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-label={item.ariaLabel}
            tabIndex={selected ? 0 : -1}
            disabled={item.disabled}
            $variant={variant}
            $selected={selected}
            $preserveDisabledAppearance={preserveDisabledAppearance}
            onClick={() => onValueChange(item.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            {item.icon}
            <span>{item.label}</span>
          </TabButton>
        );
      })}
    </TabList>
  );
};
